import React, {useEffect, useState, useMemo} from 'react';
import {Line, Tooltip, CartesianGrid, XAxis,YAxis, ResponsiveContainer, Bar, ComposedChart, Cell, ReferenceArea, Customized} from 'recharts';
import colors from '../../../../../example_data/colors';
import { Skeleton } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import '../styling/graphStyle.css'
import { electricUnitTypes, gasUnitTypes, waterUnitTypes, steamUnitTypes, temperatureUnitTypes } from '../../../../../example_data/dropdowns/clientModal/loggingComponents';
 
const Graph = props => {
    const metricValueKey = React.useMemo(() => {
        const m = props.plotSpecs.metricType;
        if (m === 'kWh') return 'power_kwh';
        if (m === 'kW')  return 'kw';
        return 'current';
    }, [props.plotSpecs.metricType]);
 
    // Measure container height and pass directly to ResponsiveContainer
    // This is needed because ResponsiveContainer doesn't re-measure when parent layout changes (e.g. toolbar toggle)
    const containerRef = React.useRef(null);
    const [containerHeight, setContainerHeight] = useState(400);
    // For mobile rendering of the graph
    const [isMobile, setIsMobile] = useState(() => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        return width <= 900 || (width / height > 1 && height <= 500);
    });

    useEffect(() => {
        if (!containerRef.current) return;
        
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                setContainerHeight(entry.contentRect.height);
            }
        });
        
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // For mobile rendering
    useEffect(() => {
        const checkIsMobile = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            return width <= 900 || (width / height > 1 && height <= 500);
        };

        const handleResize = () => setIsMobile(checkIsMobile());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    const formatXAxisLabel = (time) => {
        let offset = new Date().getTimezoneOffset();
        let date = new Date(time);
        date.setMinutes(date.getMinutes() - offset);
 
        if(props.plotSpecs.timeframe === '7d' && props.plotSpecs.resolution === '1d'){
            return(date.toDateString() === new Date().toDateString()) ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' });
        }
 
        let timeFieldObj = {};
 
        if(!props.plotSpecs.resolution.endsWith('d')){
            timeFieldObj.hour = '2-digit';
            timeFieldObj.minute = '2-digit';
            timeFieldObj.timeZone = props.selectedDates.tzName;
        }
 
        if(props.plotSpecs.timeframe.endsWith('d') || props.plotSpecs.timeframe === 'custom'){
            timeFieldObj.month = '2-digit';
            timeFieldObj.day = '2-digit';
 
            if(props.plotSpecs.timeframe === '365d' || (props.plotSpecs.timeframe === 'custom' && daysBetweenCustom() > 31)) timeFieldObj.year = 'numeric';
        }
 
        const formattedTime = date.toLocaleString('en-UK', timeFieldObj);
 
        return formattedTime;
    }
 
    const removeLastDot = str => {
        const lastDotIndex = str.lastIndexOf('.');
 
        if (lastDotIndex !== -1) {
            return str.slice(0, lastDotIndex);
        }
 
        return str;
    }
 
    const formatReadings = (reading, name, e) => {
        if(name === 'events.value'){
            return [reading, 'Events']
        };
 
        let fixedName = removeLastDot(name);
 
        let totalCheck = fixedName.split('-');
        
        if(totalCheck.length === 2 && totalCheck[0] === 'total'){
            let unitLookup = props.plotSpecs.activeUtility === 'gas' ? gasUnitTypes : props.plotSpecs.activeUtility === 'water' ? waterUnitTypes : props.plotSpecs.activeUtility === 'electricity' ? electricUnitTypes : props.plotSpecs.activeUtility === 'steam' ? steamUnitTypes : temperatureUnitTypes;
 
            if(unitLookup.some(u => u.value === totalCheck[1])){
                fixedName = unitLookup.find(u => u.value === totalCheck[1])?.text ?? totalCheck[1]
            }
        }
 
        return [(Math.round(reading * 100) / 100).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","), fixedName];
    }
 
    const formatYAxisTick = tick => {
        if (Math.abs(tick) >= 1e6) return (tick / 1e6).toFixed(0) + 'M'; 
        else if (Math.abs(tick) >= 1e4) return (tick / 1e3).toFixed(0) + 'k';
        else if (Math.abs(tick) >= 1e3) return (tick).toFixed(0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        else return Math.round(tick).toLocaleString();
    }
 
    const CustomizedTick = params => {
        const { x, y, payload } = params;
 
        const formattedTime = formatXAxisLabel(payload.value)
 
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={16} fill="rgb(78,79,83)" fontSize='12px'>
                {
                    (props.plotSpecs.timeframe.endsWith('d') && !props.plotSpecs.resolution.endsWith('d')) ? 
                    <>
                        <tspan textAnchor="middle" x="0">
                            {formattedTime.split(' ')[1]}
                        </tspan>
                        <tspan textAnchor="middle" x="0" dy="14">
                            {formattedTime.split(' ')[0].substring(0, formattedTime.split(' ')[0].length - 1)}
                        </tspan>
                    </> : 
                    <tspan textAnchor="middle" x="0">
                        {formattedTime}
                    </tspan>
                }
                </text>
            </g>
        );
    }
 
    const CloseSvg = ({ onClose, offset }) => {
        const x = (offset?.left ?? 0) + 6;
        const y = (offset?.top ?? 0) + 6;
        const size = 18;
 
        const stop = e => e.stopPropagation();
 
        return(
            <g
                transform={`translate(${x},${y})`}
                onMouseDown={stop}
                onMouseUp={stop}
                onMouseMove={stop}
                onClick={e => { stop(e); onClose?.(); }}
                style={{ cursor: 'pointer' }}
            >
                <rect x={-2} y={-2} width={size+4} height={size+4} rx={4}
                        fill="rgba(217, 29, 29, 0.85)" stroke="rgba(0,0,0,0)" style={{padding:'1vh'}}/>
                <line x1={0} y1={0} x2={size} y2={size-0} stroke="white" strokeWidth={1.5} strokeLinecap='round'/>
                <line x1={size} y1={0} x2={0} y2={size} stroke="white" strokeWidth={1.5} strokeLinecap='round'/>
                <title>Close</title>
            </g>
        );
    }
 
    const chartData = useMemo(() => {
      if (!Array.isArray(props.data)) return [];
      const toMs = (t) => typeof t === 'number'
          ? (t < 1e12 ? t * 1000 : t) // seconds -> ms if needed
          : new Date(t).getTime();
 
      const rows = props.data.map(r => ({ ...r, time: toMs(r.time) }));
      rows.sort((a, b) => a.time - b.time);
 
      return rows;
    }, [props.data]);
 
    const leftKeys = useMemo(() => (
        (props.loggedUnits || []).filter(u => u?.checked).map(u => `${u.name}.${metricValueKey}`)
    ), [props.loggedUnits, metricValueKey]);
 
    const getAxisYDomainMulti = (x1, x2, keys, pad = 0) => {
        if (!chartData.length || !keys.length) return [0, props.maxYValue];
 
        const from = Math.min(Number(x1), Number(x2));
        const to = Math.max(Number(x1), Number(x2));
 
        let min = Infinity, max = -Infinity;
        for (const row of chartData) {
            const t = row.time;
            if (t < from || t > to) continue;
 
            for (const k of keys){
                let [compName, keyName] = k.split('.');
 
                let value = row[compName]
                if(value === undefined) continue;
 
                const v = parseFloat(value[keyName]);
 
                if(v == null || Number.isNaN(v)) continue;
                if(v < min) min = v;
                if(v > max) max = v;
            }
        }
 
        if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, props.maxYValue];
 
        const range = Math.max(1, max - min);
        const extra = Math.max(pad, range * 0.05);
 
        return [Math.max(0, Math.floor(min - extra)), Math.ceil(max + extra)];
    }
 
    const processZoomSummary = (x1, x2, keys, wholeSeries=false) => {
        let payload = {};
 
        const from = Math.min(Number(x1), Number(x2));
        const to = Math.max(Number(x1), Number(x2));
 
        try{
            for (const row of chartData) {
                const t = row.time;
                if (!wholeSeries && (t < from || t > to)) continue;
 
                for(const k of keys){
                    let [compName, keyName] = k.split('.');
 
                    let value = row[compName];
                    if(value === undefined) continue;
 
                    const v = parseFloat(value[keyName]);
 
                    if(v == null || Number.isNaN(v)) continue;
 
                    if(payload.hasOwnProperty(compName)){
                        ++payload[compName].dataPoints;
 
                        payload[compName].total += v;
 
                        if(v < payload[compName].min) payload[compName].min = v;
                        if(v > payload[compName].max) payload[compName].max = v;
                    }else{
                        payload[compName] = {
                            dataPoints: 1,
                            total: v,
                            max: v,
                            min: v,
                        };
                    }
                }
            }
 
            let multiplier = 1;
            let res = props.plotSpecs.resolution;
 
            if(props.plotSpecs.activeUtility === 'water' && ['L/min', 'm³/h'].includes(props.plotSpecs.metricType)){
                if(props.plotSpecs.metricType === 'L/min'){
                    if(res === '1m') multiplier = 1;
                    else if(res === '5m') multiplier = 5;
                    else if(res === '15m') multiplier = 15;
                    else if(res === '1h') multiplier = 60;
                    else if(res === '1d') multiplier = 60 * 24;
 
                }else if(props.plotSpecs.metricType === 'm³/h'){
                    if(res === '1m') multiplier = 1 / 60;
                    else if(res === '5m') multiplier = 5 / 60;
                    else if(res === '15m') multiplier = 15 / 60;
                    else if(res === '1h') multiplier = 1;
                    else if(res === '1d') multiplier = 24;
 
                }
            }else if(props.plotSpecs.activeUtility === 'gas' && ['m³/h'].includes(props.plotSpecs.metricType)){
                if(res === '1m') multiplier = 1 / 60;
                else if(res === '5m') multiplier = 5 / 60;
                else if(res === '15m') multiplier = 15 / 60;
                else if(res === '1h') multiplier = 1;
                else if(res === '1d') multiplier = 24;
            }
 
            for(let comp in payload){
                payload[comp].avg = payload[comp].total / payload[comp].dataPoints;
                payload[comp].total *= multiplier;
            }
 
            return payload;
        }catch(error){
            return 'error';
        }
    }
    
    useEffect(() => {
        // if new data, show the whole X range and keep Y in fallback mode
        props.setHighlightState(prev => ({
            ...prev,
            left: 'dataMin',
            right: 'dataMax',
            bottom: 'auto',
            top: 'auto',
            bottom2: 'auto',
            top2: 'auto',
            refAreaLeft: '',
            refAreaRight: '',
        }));
 
        let insights = processZoomSummary(0, 0, leftKeys, true);
        props.setInsights(insights);
 
    }, [chartData]);
 
    const zoom = () => {
        let { refAreaLeft, refAreaRight } = props.highlightState;
 
        if(!refAreaLeft || !refAreaRight || refAreaLeft === refAreaRight) {
            props.setHighlightState(prev => ({ ...prev, refAreaLeft: '', refAreaRight: '' }));
            return;
        }
        
        if(refAreaLeft > refAreaRight) [refAreaLeft, refAreaRight] = [refAreaRight, refAreaLeft];
 
        const [bottom, top]   = getAxisYDomainMulti(refAreaLeft, refAreaRight, leftKeys, 1);
 
        let insights = processZoomSummary(refAreaLeft, refAreaRight, leftKeys);
        props.setInsights(insights);
        
        props.setHighlightState(prev => ({
            ...prev,
            left: refAreaLeft,
            right: refAreaRight,
            bottom, top, bottom2, top2,
            refAreaLeft: '', refAreaRight: ''
        }));
 
        props.setAccordionKey('2');
    }
 
    const zoomOut = () => {
        props.setHighlightState(prev => ({
            ...prev,
            left: 'dataMin',
            right: 'dataMax',
            bottom: 'auto',
            top: 'auto',
            bottom2: 'auto',
            top2: 'auto',
            refAreaLeft: '',
            refAreaRight: ''
        }));
 
        props.setAccordionKey('0');
 
        let insights = processZoomSummary(0, 0, leftKeys, true);
        props.setInsights(insights);
    }
 
    const {
        refAreaLeft,
        refAreaRight,
        left,
        right,
        top,
        bottom,
        top2,
        bottom2,
    } = props.highlightState;
 
    const isZoomed = typeof left === 'number' && typeof right === 'number';
 
    const pickX = e => {
        const t = e?.activePayload?.[0]?.payload?.time;
        return Number.isFinite(t) ? t : Number(e?.activeLabel);
    }
 
    const daysBetweenCustom = () => {
        return Math.abs(props.selectedDates.end.diff(props.selectedDates.start, 'day', true));
    }
 
    const determineInterval = useMemo(() => {
        if(props.plotSpecs.timeframe === '1h'){
            if(props.plotSpecs.resolution === '1m') return 0;
            return 0;
 
        }else if(props.plotSpecs.timeframe === '4h'){
            if(props.plotSpecs.resolution === '1m') return 29;
            else if(props.plotSpecs.resolution === '5m') return 4;
            else if(props.plotSpecs.resolution === '15m') return 0;
 
        }else if(props.plotSpecs.timeframe === '1d'){
            if(props.plotSpecs.resolution === '1m') return 239;
            else if(props.plotSpecs.resolution === '5m') return 47;
            else if(props.plotSpecs.resolution === '15m') return 15;
            else if(props.plotSpecs.resolution === '1h') return 3;
            
        }else if(props.plotSpecs.timeframe === '7d'){
            if(props.plotSpecs.resolution === '1h') return 11;
            else if(props.plotSpecs.resolution === '1d') return 0;
            
        }else if(props.plotSpecs.timeframe === '31d'){
            if(props.plotSpecs.resolution === '1h') return 47;
            else if(props.plotSpecs.resolution === '1d') return 1;
            
        }else if(props.plotSpecs.timeframe === '365d'){
            if(props.plotSpecs.resolution === '1d') return 59;
            
        }else if(props.plotSpecs.timeframe === 'custom'){
            let daysBetween = daysBetweenCustom();
 
            if(props.plotSpecs.resolution === '1m') return 480 * Math.max(parseInt(daysBetween / 2), 1) - 1;
            else if(props.plotSpecs.resolution === '5m') return 96 * Math.max(parseInt(daysBetween / 2), 1) - 1;
            else if(props.plotSpecs.resolution === '15m') return 32 * Math.max(parseInt(daysBetween / 2), 1) - 1;
            else if(props.plotSpecs.resolution === '1h') return 8 * Math.max(parseInt(daysBetween / 2), 1) - 1;
            //1d available after at least 8 days
            else if(props.plotSpecs.resolution === '1d') return daysBetween < 10 ? 0 : Math.floor(daysBetween / 10);
        }
 
    }, [props.plotSpecs]);

    // Increase interval size on mobile so the x-axis doesn't get as bunched up
    const effectiveInterval = isMobile
    ? (determineInterval === 0 ? 1 : determineInterval * 2 + 1)
    : determineInterval;

    // Properly balance the Y-Axis
    const dynamicMaxY = useMemo(() => {
        if (!chartData.length) return props.maxYValue;

        let max = -Infinity;
        for (const row of chartData) {
            for (const key of leftKeys) {
                const [compName, keyName] = key.split('.');
                const v = parseFloat(row[compName]?.[keyName]);
                if (Number.isFinite(v) && v > max) max = v;
            }
        }

        if (!Number.isFinite(max)) return props.maxYValue;

        return Math.ceil(max);
    }, [chartData, leftKeys, props.maxYValue]);

    // Render ticks properly
    const dynamicYTicks = useMemo(() => {
        if (!Number.isFinite(dynamicMaxY) || dynamicMaxY === 0) return undefined;

        const magnitude = Math.pow(10, Math.floor(Math.log10(dynamicMaxY)));
        const normalized = dynamicMaxY / magnitude;

        let step;
        if (normalized <= 1)        step = 0.2 * magnitude;
        else if (normalized <= 2)   step = 0.5 * magnitude;
        else if (normalized <= 3)   step = 0.5 * magnitude;
        else if (normalized <= 5)   step = 1   * magnitude;
        else                        step = 2   * magnitude;

        const ticks = [];
        for (let i = 0; i * step < dynamicMaxY + step; i++) {
            ticks.push(i * step);
        }
        return ticks;
    }, [dynamicMaxY]);

    const yDomainTop = dynamicYTicks ? dynamicYTicks[dynamicYTicks.length - 1] : dynamicMaxY;

    // Create mobile ticks so they overlay on top of data -> so you can still read them
    const MobileYTick = ({ x, y, payload }) => {
        return (
            <text
                x={x + 4}
                y={y}
                dy={4}
                fontSize="10px"
                fill="rgb(78,79,83)"
                stroke="white"
                strokeWidth={3}
                paintOrder="stroke"
            >
                {formatYAxisTick(payload.value)}
            </text>
        );
    };

    const MobileYLabels = ({ yAxisMap }) => {
        const axis = yAxisMap?.['left'];
        if (!axis) return null;

        return (
            <g>
                {(dynamicYTicks || []).map(value => {
                    const y = axis.scale(value);
                    if (!Number.isFinite(y)) return null;
                    const adjustedY = Math.max(y, 14);
                    return (
                        <text
                            key={value}
                            x={isMobile ? 8 : axis.width - 10}  // ← replace existing x line
                            y={adjustedY}
                            dy={4}
                            fontSize={isMobile ? '11px' : '13px'}
                            textAnchor={isMobile ? 'start' : 'end'}  // ← replace existing textAnchor line
                            fill="rgb(78,79,83)"
                            stroke={isMobile ? 'white' : 'none'}
                            strokeWidth={isMobile ? 3 : 0}
                            paintOrder="stroke"
                        >
                            {formatYAxisTick(value)}
                        </text>
                    );
                })}
            </g>
        );
    };

    return(
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
        <ResponsiveContainer width="100%" height={containerHeight || '100%'}>
            {props.errorMessage !== '' ? <div id="raw-data-graph-error-msg-container">
                <WarningIcon style={{height:'15%', width:'auto', marginTop:'-12%', color:'red'}}/>
                <br/>
                <h4 style={{textAlign:'center'}}>{props.errorMessage}</h4>
            </div> : 
 
            (props.data == null ? true : props.data.length === 0) ? 
                <Skeleton variant="rounded" animation='wave' width={210} height={59}/> :
            
                <ComposedChart 
                    data={chartData}
                    id='metrics-linechart' 
                    margin={{ top: 20, right: 15, left: isMobile ? 0 : 0, bottom: 10 }}
                    animation={{duration: 250}} 
                    onMouseDown={e => {
                        const x = pickX(e);
                        if (!Number.isFinite(x)) return;
 
                        props.setHighlightState(p => ({ ...p, refAreaLeft: x, refAreaRight: '' }));
                    }}
                    onMouseMove={e => {
                        if (!props.highlightState.refAreaLeft) return;
 
                        const x = pickX(e);
                        if (!Number.isFinite(x)) return;
 
                        props.setHighlightState(p => ({ ...p, refAreaRight: x }));
                    }}
                    onMouseUp={zoom}
                >
                    <CartesianGrid strokeDasharray="2 8" />
 
                    <XAxis
                        dataKey="time"
                        type="number"
                        domain={[left, right]}
                        allowDataOverflow
                        interval={effectiveInterval}
                        ticks={chartData.map(d => d.time)}
                        padding={
                            (props.plotSpecs.resolution === '1d' && chartData.length <= 20) ?
                            { left: 75, right: 75 } :
                            { left: 30, right: 30 }
                        }
                        grid={false}
                        className="x-axis"
                        tick={<CustomizedTick />}
                    />
                    
                    <YAxis
                        grid={false}
                        allowDecimals={false}
                        yAxisId="left"
                        className="y-axis"
                        mirror={isMobile}
                        width={isMobile ? 1 : 60}
                        tick={{ fill: 'transparent'}}
                        ticks={dynamicYTicks}
                        domain={isZoomed ? [bottom, top] : [0, yDomainTop]}
                        tickFormatter={isMobile ? undefined : formatYAxisTick}
                        label={!isMobile ?
                            { value: `${props.plotSpecs.metricType}`, position: 'outside', offset: 0, angle: -90, dx: -23, style: { fontSize: '0.85em', fill: 'rgb(78,79,83)' } } :
                            undefined
                        }
                        axisLine={isMobile ? false : true}
                        tickLine={isMobile ? false : true}
                    />
 
                    {!isMobile && (props.plotSpecs.resolution !== '1d' || chartData.length > 20) &&<YAxis
                        yAxisId="right"
                        orientation="right"
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                        tick={{ fontSize: '12px', fill: 'rgb(78,79,83)'}}
                        label={{ value: `Events`, position: 'outside', offset: 0, angle: 90, dx: 30, style: { fontSize: '0.85em', fill: 'rgb(78,79,83)' } }}
                    />}
 
                    <Tooltip 
                        contentStyle={{
                            backgroundColor: '#F5F5F5',
                            color: '#333',
                            border:'1px solid lightgrey',
                            fontSize: '0.75em',
                        }} 
                        wrapperStyle={{ outline: "none" }}
                        labelFormatter={formatXAxisLabel}
                        formatter={formatReadings}
                    />
 
                    {props.loggedUnits.map((unit, i) => {
                        if(unit.checked){
                            if(props.plotSpecs.resolution === '1d' && chartData.length <= 20 && unit.name !== 'total-main'){
                                return <Bar 
                                    stackId='1' yAxisId='left' type="monotone" connectNulls 
                                    dataKey={`${unit.name}.${metricValueKey}`} 
                                    stroke={`${colors[i % colors.length]}`} fill={`${colors[i % colors.length]}`} 
                                    strokeWidth={1.5} dot={false}
                                />
                            }
 
                            return <Line 
                                yAxisId='left' type="monotone" connectNulls 
                                dataKey={`${unit.name}.${metricValueKey}`} 
                                stroke={`${colors[i % colors.length]}`} strokeWidth={1.5} dot={false} 
                            />
                        }
                        
                    })}
 
                    {!isMobile && (props.plotSpecs.resolution !== '1d' || chartData.length > 20) && <Bar
                        yAxisId='right' type="monotone" dataKey={`events.value`} 
                        barSize={100}
                        style={{cursor:'pointer'}}
                        onMouseEnter={(_, index) => props.setEventIndex(index)}
                        onMouseLeave={() => props.setEventIndex(null)}
                        onClick={dataPoint => {
                            props.setAccordionKey('1');
                            props.setSelectedEvents(dataPoint.payload.events.ids);
                        }}
                    >
                        {chartData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={index === props.eventIndex ? 'rgba(247,230,83,0.4)':'rgba(213,43,0,0.4)'} style={{cursor:'pointer'}}/>
                        ))}
                    </Bar>}
 
                    {refAreaLeft && refAreaRight && (
                        <ReferenceArea
                            yAxisId="left"
                            x1={Math.min(refAreaLeft, refAreaRight)}
                            x2={Math.max(refAreaLeft, refAreaRight)}
                            fill="rgba(136,132,216,0.15)"
                            stroke="rgba(136,132,216,0.6)"
                            strokeOpacity={0.8}
                            ifOverflow="extendDomain"
                        />
                    )}
 
                    {isZoomed && <Customized component={<CloseSvg onClose={() => zoomOut()} />} />}
                    <Customized component={MobileYLabels} />
                </ComposedChart>
            }
        </ResponsiveContainer>
        </div>
    );
}
 
export default Graph;